import { fetch } from '@atlassian/eoc-fetch';

import { urls } from '../../../common/constants/urls';
import type { ResponderType } from '../../../common/types';
import type {
	AddOverrideBulkParameters,
	AddOverrideParameters,
	CloneScheduleParameters,
	CloneScheduleResponse,
	CreateScheduleParameters,
	CreateScheduleResponse,
	DeleteScheduleParameters,
	ExportTimelineParameters,
	GetCalendarResponse,
	GetOverrideParameters,
	GetOverrideResponse,
	GetTimelineParameters,
	GetTimelineResponse,
	GetWebCalendarUrlParameters,
	GetWebCalendarUrlResponse,
	RevertOverrideParameters,
	TakeOnCallParameters,
	UpdateOverrideParameters,
	UpdateScheduleParameters,
	UpdateScheduleResponse,
} from '../../../common/types/schedule';

const getTimeline = ({
	scheduleId,
	currentDate,
	mode,
	timelineType,
	rotation,
}: GetTimelineParameters) => {
	return fetch<GetTimelineResponse>(urls.schedules.scheduleId.timeline.url({ scheduleId }), {
		method: 'POST',
		body: {
			scheduleId,
			currentDate,
			mode,
			timelineType,
			rotation,
		},
	});
};

const getCalendar = ({
	scheduleId,
	currentDate,
	mode,
	timelineType,
	rotation,
}: GetTimelineParameters) => {
	return fetch<GetCalendarResponse>(urls.schedules.scheduleId.timeline.url({ scheduleId }), {
		method: 'POST',
		body: {
			scheduleId,
			currentDate,
			mode,
			timelineType,
			rotation,
		},
	});
};

const takeOnCall = ({ accountId, scheduleId }: TakeOnCallParameters) => {
	return fetch(urls.schedules.scheduleId.overrides.url({ scheduleId }), {
		method: 'POST',
		body: {
			hours: 1,
			responder: {
				id: accountId,
				type: 'user' as ResponderType,
			},
		},
	});
};

const addOverride = ({
	endTime,
	responder,
	rotationIds,
	scheduleId,
	startTime,
	timezone,
}: AddOverrideParameters) => {
	return fetch(urls.schedules.scheduleId.overrides.url({ scheduleId }), {
		method: 'POST',
		body: {
			endTime,
			responder,
			rotationIds,
			startTime,
			timezone,
		},
	});
};

const addOverrideBulk = ({ overrides, scheduleId }: AddOverrideBulkParameters) => {
	return fetch(urls.schedules.scheduleId.overrides.bulk.url({ scheduleId }), {
		method: 'POST',
		body: {
			overrides,
		},
	});
};

const getOverride = ({
	alias,
	scheduleId,
}: GetOverrideParameters): Promise<GetOverrideResponse> => {
	return fetch(urls.schedules.scheduleId.overrides.alias.url({ alias, scheduleId }), {
		method: 'GET',
	});
};

const revertOverride = ({ alias, scheduleId }: RevertOverrideParameters) => {
	return fetch(urls.schedules.scheduleId.overrides.alias.url({ alias, scheduleId }), {
		method: 'DELETE',
	});
};

const updateOverride = ({
	alias,
	endTime,
	responder,
	rotationIds,
	scheduleId,
	startTime,
	timezone,
}: UpdateOverrideParameters) => {
	return fetch(urls.schedules.scheduleId.overrides.alias.url({ alias, scheduleId }), {
		method: 'PUT',
		body: {
			endTime,
			responder,
			rotationIds,
			startTime,
			timezone,
		},
	});
};

const exportTimeline = async ({ scheduleId }: ExportTimelineParameters) => {
	const response = await fetch<BlobPart>(urls.schedules.scheduleId.timeline.url({ scheduleId }), {
		method: 'GET',
		responseType: 'headers',
		headers: {
			'Content-Type': 'application/octet-stream',
		},
	});

	const filename = response.headers
		?.get('content-disposition')
		?.split(';')[2]
		.trim()
		.split('=')[1]
		.replace(/"/g, '');

	const content = await response.text();

	return { content, filename };
};

const updateSchedule = ({
	scheduleId,
	description,
	name,
	enabled,
	timezone,
	teamId,
}: UpdateScheduleParameters) => {
	return fetch<UpdateScheduleResponse>(urls.schedules.scheduleId.url({ scheduleId }), {
		method: 'PATCH',
		body: {
			description,
			name,
			enabled,
			timezone,
			teamId,
		},
	});
};

const deleteSchedule = ({ scheduleId }: DeleteScheduleParameters) => {
	return fetch(urls.schedules.scheduleId.url({ scheduleId }), {
		method: 'DELETE',
	});
};

const createSchedule = ({ description, name, teamId, timezone }: CreateScheduleParameters) => {
	return fetch<CreateScheduleResponse>(urls.schedules.url(), {
		method: 'POST',
		body: {
			description,
			name,
			teamId,
			timezone,
			// Created schedules are enabled by default
			enabled: true,
		},
	});
};

const cloneSchedule = ({
	scheduleId,
	description,
	name,
	enabled,
	timezone,
	teamId,
}: CloneScheduleParameters) => {
	return fetch<CloneScheduleResponse>(urls.schedules.scheduleId.clone.url({ scheduleId }), {
		method: 'POST',
		body: {
			description,
			name,
			enabled,
			timezone,
			teamId,
		},
	});
};

const getWebCalendarUrl = ({ scheduleId }: GetWebCalendarUrlParameters) => {
	if (scheduleId) {
		return fetch<GetWebCalendarUrlResponse>(
			urls.schedules.scheduleId.webCalendarUrl.url({ scheduleId }),
			{
				method: 'POST',
			},
		);
	}

	return fetch<GetWebCalendarUrlResponse>(urls.schedules.user.webCalendarUrl.url(), {
		method: 'POST',
	});
};

export const endpoints = {
	addOverride,
	addOverrideBulk,
	exportTimeline,
	getCalendar,
	getOverride,
	getTimeline,
	revertOverride,
	takeOnCall,
	updateOverride,
	updateSchedule,
	deleteSchedule,
	createSchedule,
	cloneSchedule,
	getWebCalendarUrl,
};
